import type { ComponentProps } from 'react';

import { Root, Slottable } from '@radix-ui/react-slot';
import { twJoin, twMerge } from 'tailwind-merge';
import { tv, type VariantProps } from 'tailwind-variants';

export const buttonVariants = tv({
  base: twJoin(
    'inline-flex outline-hidden focus-visible:outline-primary-600',
    'rounded-md py-2 px-4 transition duration-75',
    'disabled:opacity-30 disabled:cursor-not-allowed',
  ),

  variants: {
    noAnimation: {
      true: '',
      false: 'active:transform active:scale-95',
    },
    fill: {
      filled: 'bg-primary-600 text-white hover:bg-primary-600/90 active:bg-primary-600/80',
      outlined: twJoin(
        'border border-primary-600 text-primary-600',
        'hover:border-primary-600/90 active:border-primary-600/80 hover:text-primary-600/90',
        'data-[state=on]:bg-primary-600/90 data-[state=on]:text-white',
        'data-[state=on]:border-primary-600/10 data-[state=on]:hover:bg-primary-600',
      ),
      ghost: twJoin(
        'text-primary-600',
        'hover:text-foreground hover:bg-gray-100/70',
        'aria-checked:bg-primary-600 aria-checked:text-white',
        'aria-expanded:bg-gray-100/70 aria-expanded:text-gray-900',
      ),
    },
    size: {
      sm: 'text-sm',
      md: 'text-base',
      lg: 'text-lg',
      xl: 'text-xl',
    },
    align: {
      left: 'justify-start',
      center: 'justify-center',
      right: 'justify-end',
    },
    items: {
      center: 'items-center',
    },
    textColor: {
      inherit: 'text-inherit',
      primary: 'text-primary-600',
      foreground: 'text-foreground',
      background: 'text-background',
      gray: 'text-gray-500',
    },
    underlined: {
      true: 'underline underline-offset-2',
    },
    buttonColor: {
      gray: '',
      black: '',
      foreground: 'bg-foreground',
    },
  },
  compoundVariants: [
    {
      fill: 'outlined',
      class: twJoin('hover:bg-primary-600 hover:text-white'),
    },
    {
      buttonColor: 'gray',
      fill: 'outlined',
      class: twJoin(
        'border border-gray-300',
        'hover:border-gray-500 hover:bg-gray-50 hover:text-inherit',
        'active:border-gray-500 active:bg-gray-50',
        'aria-expanded:bg-gray-100 aria-expanded:border-gray-600',
      ),
    },
    {
      buttonColor: 'black',
      fill: 'outlined',
      class: twJoin(
        'border border-gray-500',
        'hover:border-gray-900 hover:bg-gray-100 hover:text-inherit',
        'active:border-gray-900 active:bg-gray-100',
        'aria-expanded:bg-gray-200 aria-expanded:border-gray-900',
      ),
    },
    {
      buttonColor: 'foreground',
      fill: 'filled',
      class: twJoin('bg-foreground hover:bg-foreground/90 active:bg-foreground/80'),
    },
    {
      fill: 'filled',
      buttonColor: 'gray',
      class: twJoin(
        'bg-gray-100 hover:bg-gray-200 text-gray-600',
        'active:bg-gray-300 active:text-gray-800',
        'aria-expanded:bg-gray-300 aria-expanded:text-gray-800',
      ),
    },
    {
      fill: 'filled',
      buttonColor: 'black',
      class: twJoin(
        'bg-gray-800 hover:bg-gray-900 text-gray-50',
        'active:bg-gray-900 active:text-gray-50',
        'aria-expanded:bg-gray-900 aria-expanded:text-gray-50',
      ),
    },
  ],
  defaultVariants: {
    fill: 'filled',
    noAnimation: false,
    align: 'center',
  },
});

export interface ButtonProps extends VariantProps<typeof buttonVariants>, ComponentProps<'button'> {
  asChild?: boolean;
  children?: React.ReactNode;
  inputRef?: React.Ref<HTMLButtonElement>;
  startIcon?: React.ReactNode;
}

export function Button({
  children,
  asChild = false,
  buttonColor,
  className,
  items,
  noAnimation,
  startIcon,
  textColor,
  underlined,
  align,
  fill,
  size,
  ...props
}: ButtonProps): React.ReactElement {
  const Comp = asChild ? Root : 'button';

  const variants = buttonVariants({
    ...props,
    align,
    size,
    fill,
    buttonColor,
    noAnimation,
    textColor,
    underlined,
    items,
    className,
  });

  return (
    <Comp
      data-slot="button"
      className={twMerge(variants, startIcon && children ? 'gap-1.5 items-center' : '')}
      {...props}
    >
      {startIcon}
      <Slottable>{children}</Slottable>
    </Comp>
  );
}
